import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { PrivateEntityId } from './encrypted-entities.js';
/**
 * Visual review (package D), decision half: design-option boards (one decision whose options
 * carry images or HTML boards, answered by a pick or a mix), the `viko_options` MCP tool and the
 * review-server status shape. Additive only.
 */
// ---- design-option boards -----------------------------------------------------------------------
export const OptionMediaKind = z.enum(['image', 'html']);
/** One visual of an option: a screenshot/mockup or a live HTML board, in a colour scheme. */
export const OptionMedia = z.strictObject({
    kind: OptionMediaKind,
    /** The artifact id (published by the daemon) or, before publishing, a worktree path. */
    ref: z.string().min(1).max(2000),
    label: z.string().max(200).optional(),
    theme: z.enum(['light', 'dark']).optional(),
    mime: z.string().max(255).optional(),
    /** Sealed coordinates the encrypted viewer opens. */
    artifact: EncryptedArtifactRef.optional(),
});
export const OPTION_MEDIA_MAX = 8;
/**
 * Option-set metadata on a decision: design areas the user may mix by ("buttons", "toolbars"),
 * the tasks the answer fans out to, and the sign-off gate.
 */
export const OptionSet = z.strictObject({
    kind: z.literal('design'),
    /** Mixable areas; empty = pick one option for everything. */
    areas: z.array(z.string().min(1).max(80)).max(12),
    /** Tasks the answer is written into (the affected tasks and boards). */
    fanOut: z.array(PrivateEntityId).max(50),
    /** Implementation tasks that wait (blocked) until the mockup is signed off. */
    gate: z
        .strictObject({
        taskIds: z.array(PrivateEntityId).max(50),
        /** Gated tasks' status before the gate (restored on sign-off). */
        held: z.record(PrivateEntityId, z.string().max(40)).optional(),
        state: z.enum(['waiting', 'signed-off', 'released']),
        at: z.number().int().nonnegative().optional(),
    })
        .optional(),
});
/** The user's choice per area ("C for buttons, D for toolbars"). */
export const OptionMix = z.array(z.strictObject({
    area: z.string().min(1).max(80),
    optionId: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9:_-]+$/),
}));
export const OPTION_COMMENTS_MAX = 20;
/** `{o1_3: "wider padding"}`: per-option comments sent with the pick. */
export const OptionComments = z
    .record(z.string().min(1).max(128), z.string().min(1).max(2000))
    .refine((r) => Object.keys(r).length <= OPTION_COMMENTS_MAX, 'too many comments');
/** Letter for the n-th option (1 → A). */
export const optionLetter = (n) => String.fromCharCode(64 + Math.min(Math.max(n, 1), 26));
/**
 * Human text for an answer with a mix and comments: "C for buttons, D for toolbars. Comments: A:
 * wider padding". Options are named by letter and label.
 */
export function describeBoardAnswer(options, a) {
    const name = (id) => {
        const i = options.findIndex((o) => o.id === id);
        return i < 0 ? id : `${optionLetter(i + 1)} (${options[i]?.label})`;
    };
    const parts = [];
    if (a.mix?.length)
        parts.push(a.mix.map((m) => `${name(m.optionId)} for ${m.area}`).join(', '));
    else if (a.optionId)
        parts.push(name(a.optionId));
    const cs = Object.entries(a.comments ?? {});
    if (cs.length)
        parts.push(`Comments: ${cs.map(([id, t]) => `${name(id)}: ${t}`).join('; ')}`);
    return parts.join('. ');
}
/** MCP `viko_options`: an agent posts an option set (A–D…) as one decision. */
export const OptionInput = z.object({
    label: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    rationale: z.string().max(4000).optional(),
    pros: z.array(z.string()).max(10).optional(),
    cons: z.array(z.string()).max(10).optional(),
    recommended: z.boolean().optional(),
    /** Worktree paths of images (.png/.jpg/...) for this option. */
    images: z
        .array(z.object({
        path: z.string().min(1).max(2000),
        label: z.string().max(200).optional(),
        theme: z.enum(['light', 'dark']).optional(),
    }))
        .max(OPTION_MEDIA_MAX)
        .optional(),
    /** Worktree paths of self-contained HTML boards, optionally one per colour scheme. */
    html: z
        .array(z.object({
        path: z.string().min(1).max(2000),
        label: z.string().max(200).optional(),
        theme: z.enum(['light', 'dark']).optional(),
    }))
        .max(2)
        .optional(),
});
export const VikoOptionsInput = z.object({
    question: z.string().min(1).max(4000),
    title: z.string().max(120).optional(),
    why: z.string().max(4000).optional(),
    options: z.array(OptionInput).min(2).max(9),
    /** Areas the user may mix by ("buttons", "toolbars"). */
    areas: z.array(z.string().min(1).max(80)).max(12).optional(),
    /** Tasks the answer is written into. */
    affectsTasks: z.array(z.string().min(1).max(256)).max(50).optional(),
    /** Implementation tasks that wait until the user signs the mockup off. */
    gateTasks: z.array(z.string().min(1).max(256)).max(50).optional(),
    /** Default false: keep working; the answer arrives as a message. */
    blocking: z.boolean().optional(),
    assumption: z.string().max(2000).optional(),
});
export const VikoOptionsOutput = z.object({
    questionId: z.string(),
    decisionId: z.string().optional(),
    status: z.enum(['pending', 'answered', 'cancelled']),
    /** The gate is armed: these tasks are blocked until the mockup is signed off. */
    gated: z.array(z.string()).optional(),
    published: z.number().int().nonnegative().optional(),
    note: z.string().optional(),
});
export const VISUAL_REVIEW_MCP_TOOLS = {
    viko_options: { input: VikoOptionsInput, output: VikoOptionsOutput },
};
// ---- review servers -----------------------------------------------------------------------------
export const ReviewServerState = z.enum(['down', 'needs_share', 'ready', 'invalid']);
/** One declared Mac-local review server and what it takes to open it from another device. */
export const ReviewServerStatus = z.strictObject({
    name: z.string().min(1).max(80),
    url: z.string().min(1).max(2048),
    interactive: z.boolean(),
    port: z.number().int().min(1).max(65_535).optional(),
    state: ReviewServerState,
    /** Process listening on the port (the consent names it). */
    process: z.string().max(200).optional(),
    /** `invalid`: why the declaration cannot be tunnelled. */
    problem: z.string().max(300).optional(),
});
//# sourceMappingURL=visual-review.js.map